import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { classNames } from 'primereact/utils';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import errorMessage from '../../../helpers/errorMessage';
import { Users, ALL_GROUPS } from './Users';
import PageHeader from '../../common/PageHeader';

// Defaults only - the multiselect in the dialog lets the admin change these
// before creating the user. "Exaloan" (custom:Platform for internal staff,
// same convention used everywhere else in this app) gets Dev+Prod like the
// rest of the team; any real platform account only needs Prod - matches the
// rule already applied to every existing user in the Cognito backfill.
function defaultGroupsFor(platformName) {
    const base = platformName.toLowerCase() === 'exaloan' ? ['DevAccess', 'ProdAccess'] : ['ProdAccess'];
    return [...base, 'User'];
}

const emptyNewUser = (platformName) => ({ email: '', groups: defaultGroupsFor(platformName) });

export const PlatformUsers = (props) => {

    const [platformUsers, setPlatformUsers] = useState(null)
    const [createDialogVisible, setCreateDialogVisible] = useState(false)
    const [newUser, setNewUser] = useState(null)
    const [submitted, setSubmitted] = useState(false)

    const { platformName } = useParams()

    const setLoadingInProps = props.setLoading

    const fetchUsers = () => {
        setLoadingInProps(true)
        axiosWithAuth.get(`/platforms/${platformName}/users`).then((response) => {
            setPlatformUsers(response.data);
            setLoadingInProps(false)
        }).catch((err) => {
            alert(errorMessage(err));
            setLoadingInProps(false)
        })
    }

    useEffect(() => {
        fetchUsers()
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const openCreateDialog = () => {
        setNewUser(emptyNewUser(platformName));
        setSubmitted(false);
        setCreateDialogVisible(true);
    }

    const hideCreateDialog = () => {
        setCreateDialogVisible(false);
    }

    const onCreateUser = () => {
        setSubmitted(true);
        if (!newUser.email) {
            return;
        }
        setLoadingInProps(true);
        axiosWithAuth.post(`/platforms/${platformName}/users`, newUser).then(() => {
            setCreateDialogVisible(false);
            fetchUsers();
        }).catch((err) => {
            alert('Failed to create user: ' + errorMessage(err));
            setLoadingInProps(false);
        })
    }

    const createDialogFooter = (
        <React.Fragment>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideCreateDialog} />
            <Button label="Create" icon="pi pi-check" className="p-button-text" onClick={onCreateUser} />
        </React.Fragment>
    );

    return (
        <div className="content-container">
            <div style={{ maxWidth: "100%" }}>
                <PageHeader
                    title={`${platformName} Users`}
                    subtitle="Cognito accounts and access groups for this Originator."
                    icon="pi-users"
                    actions={<Button label="Create User" icon="pi pi-user-plus" onClick={openCreateDialog} />}
                />
                {platformUsers && <Users users={platformUsers} />}
            </div>
            {newUser && <Dialog visible={createDialogVisible} style={{ width: '450px' }} header={`Create a user for ${platformName}`} modal className="p-fluid" footer={createDialogFooter} onHide={hideCreateDialog}>
                <div className="field">
                    <span className="p-float-label">
                        <InputText id="new_user_email" name="email" value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            className={classNames({ 'p-invalid': submitted && !newUser.email })} />
                        <label htmlFor="new_user_email">Email</label>
                    </span>
                    {submitted && !newUser.email && <small className="p-error">Email is required.</small>}
                </div>
                <div className="field">
                    <label htmlFor="new_user_groups">Groups</label>
                    <MultiSelect id="new_user_groups" value={newUser.groups} options={ALL_GROUPS}
                        onChange={(e) => setNewUser({ ...newUser, groups: e.value })} display="chip" />
                </div>
                <small className="text-color-secondary">An invite with a temporary password is emailed to them automatically.</small>
            </Dialog>}
        </div>
    );
}
